import {
  Component,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
  HostListener,
  Renderer2,
  ChangeDetectorRef
} from '@angular/core';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIconButton } from '@angular/material/button';
import { NgIf } from '@angular/common';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { MatIcon } from '@angular/material/icon';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AppHeaderComponent } from '../app-home/app-header/app-header.component';
import { BucketService, ProcessedFile } from '../../services/bucket.service';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AdminTreeComponent } from './admin-tree/admin-tree.component';
import { AdminTreeNode } from '../../services/admin.service';
import { AdminMainAreaComponent } from './admin-main-area/admin-main-area.component';
import { AdminDetailsComponent, ViewMode } from './admin-details/admin-details.component';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  imports: [
    CommonModule,
    MatToolbar,
    MatButtonModule,
    MatIconButton,
    NgIf,
    RouterModule,
    MatIcon,
    MatProgressSpinner,
    AppHeaderComponent,
    MatTooltipModule,
    AdminTreeComponent,
    AdminMainAreaComponent,
    AdminDetailsComponent
  ],
  standalone: true,
  styleUrl: './admin.component.css'
})
export class AdminComponent implements OnInit, AfterViewInit {
  @ViewChild('adminContainer') adminContainer!: ElementRef;
  @ViewChild(AdminMainAreaComponent) adminMainArea!: AdminMainAreaComponent;

  // Define default admin panel width constants
  readonly DEFAULT_TREE_WIDTH = 15;      // Tree navigation panel
  readonly DEFAULT_MAIN_WIDTH = 45;      // Main area panel
  readonly DEFAULT_DETAILS_WIDTH = 40;   // Details panel (larger to compensate for resizers/borders)
  readonly DEFAULT_MAIN_NO_DETAILS = 85; // Main area when details is hidden

  // Layout constraints - externalized
  readonly MIN_TREE_WIDTH = 15;
  readonly MAX_TREE_WIDTH = 25;
  readonly MIN_MAIN_WIDTH = 25;
  readonly MAX_MAIN_WIDTH = 60;
  readonly MIN_DETAILS_WIDTH = 25;
  readonly MAX_DETAILS_WIDTH = 60;

  // User information
  userRole: string = 'admin';
  userName: string = 'Admin User';

  // Layout configuration - initialized from constants
  treeAreaFlex: string = `0 0 ${this.DEFAULT_TREE_WIDTH}%`;
  mainAreaFlex: string = `0 0 ${this.DEFAULT_MAIN_WIDTH}%`;
  detailsAreaFlex: string = `0 0 ${this.DEFAULT_DETAILS_WIDTH}%`;

  // Resize state - initialized from constants
  isTreeResizing: boolean = false;
  isDetailsResizing: boolean = false;
  startX: number = 0;
  currentTreeWidth: number = this.DEFAULT_TREE_WIDTH;
  currentMainWidth: number = this.DEFAULT_MAIN_WIDTH;
  currentDetailsWidth: number = this.DEFAULT_DETAILS_WIDTH;

  // Tree and selection state
  selectedNode: AdminTreeNode | null = null;
  isTreeCollapsed: boolean = false;
  showEditMode: boolean = false;

  // Details panel state
  showDetails: boolean = true;
  selectedFile: ProcessedFile | null = null;
  viewMode: ViewMode = 'split';

  // Bucket and operation state
  bucketName: string = 'questions-images-bucket';
  bucketFolders: string[] = [];
  selectedFolder: string = '';
  processedFiles: ProcessedFile[] = [];
  isLoading: boolean = false;
  operationResult: string = '';
  showProcessedFiles: boolean = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private http: HttpClient,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private bucketService: BucketService,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    console.log('Admin component initialized');
    this.initializeUser();
    this.initializeAdminPanelWidths();

    // Clear saved layout once to ensure users get the updated layout with fixed borders
    const layoutVersion = localStorage.getItem('adminLayoutVersion');
    if (layoutVersion !== '2.1') {
      this.clearSavedAdminLayout();
      localStorage.setItem('adminLayoutVersion', '2.1');
      console.log('Cleared old admin layout for updated version 2.1');
    }

    this.loadSavedLayout();
  }

  ngAfterViewInit(): void {
    this.setupEventListeners();
  }

  private initializeUser(): void {
    const storedUser = localStorage.getItem('googleUser');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      if (user.name.length >= 20) {
        this.userName = user.name.split(' ')[0] || 'Admin';
      } else {
        this.userName = user.name || 'Admin';
      }
    }
  }

  private initializeAdminPanelWidths(): void {
    console.log('Initializing admin panel widths from constants...');

    // Set initial flex values based on default constants
    this.treeAreaFlex = `0 0 ${this.DEFAULT_TREE_WIDTH}%`;
    this.mainAreaFlex = `0 0 ${this.DEFAULT_MAIN_WIDTH}%`;
    this.detailsAreaFlex = `0 0 ${this.DEFAULT_DETAILS_WIDTH}%`;

    // Set current width values
    this.currentTreeWidth = this.DEFAULT_TREE_WIDTH;
    this.currentMainWidth = this.DEFAULT_MAIN_WIDTH;
    this.currentDetailsWidth = this.DEFAULT_DETAILS_WIDTH;

    console.log('Admin panel widths initialized:', {
      tree: this.DEFAULT_TREE_WIDTH,
      main: this.DEFAULT_MAIN_WIDTH,
      details: this.DEFAULT_DETAILS_WIDTH
    });
  }

  private loadSavedLayout(): void {
    const savedLayout = localStorage.getItem('adminPanelLayout');
    if (savedLayout) {
      try {
        const layout = JSON.parse(savedLayout);
        this.currentTreeWidth = layout.treeWidth || this.DEFAULT_TREE_WIDTH;
        this.currentMainWidth = layout.mainWidth || this.DEFAULT_MAIN_WIDTH;
        this.currentDetailsWidth = layout.detailsWidth || this.DEFAULT_DETAILS_WIDTH;
        this.showDetails = layout.showDetails !== undefined ? layout.showDetails : true;
        this.updateFlexValues();
        console.log('Loaded saved admin layout:', layout);
      } catch (error) {
        console.warn('Failed to load saved admin layout, using defaults:', error);
        this.initializeAdminPanelWidths();
      }
    } else {
      console.log('No saved admin layout found, using defaults');
    }
  }

  private saveLayout(): void {
    const layout = {
      treeWidth: this.currentTreeWidth,
      mainWidth: this.currentMainWidth,
      detailsWidth: this.currentDetailsWidth,
      showDetails: this.showDetails
    };
    localStorage.setItem('adminPanelLayout', JSON.stringify(layout));
    console.log('Saved admin layout:', layout);
  }

  private clearSavedAdminLayout(): void {
    localStorage.removeItem('adminPanelLayout');
    console.log('Cleared saved admin panel layout');
  }

  private setupEventListeners(): void {
    // Global mouse events for resizing
    this.renderer.listen('document', 'mousemove', (event) => this.onMouseMove(event));
    this.renderer.listen('document', 'mouseup', () => this.onMouseUp());
  }

  // Tree-Main divider resize
  startTreeResize(event: MouseEvent): void {
    event.preventDefault();
    this.isTreeResizing = true;
    this.startX = event.clientX;
  }

  // Main-Details divider resize
  startDetailsResize(event: MouseEvent): void {
    event.preventDefault();
    this.isDetailsResizing = true;
    this.startX = event.clientX;
  }

  private onMouseMove(event: MouseEvent): void {
    if (this.isTreeResizing) {
      this.handleTreeResize(event);
    } else if (this.isDetailsResizing) {
      this.handleDetailsResize(event);
    }
  }

  private handleTreeResize(event: MouseEvent): void {
    const container = this.adminContainer.nativeElement;
    const containerWidth = container.getBoundingClientRect().width;
    const deltaX = event.clientX - this.startX;
    const percentChange = (deltaX / containerWidth) * 100;

    let newTreeWidth = this.currentTreeWidth + percentChange;
    newTreeWidth = Math.max(this.MIN_TREE_WIDTH, Math.min(this.MAX_TREE_WIDTH, newTreeWidth));

    const availableWidth = 100 - newTreeWidth;
    let newMainWidth = this.currentMainWidth;
    let newDetailsWidth = this.currentDetailsWidth;

    if (this.showDetails) {
      // Maintain details width ratio
      const mainDetailsTotal = this.currentMainWidth + this.currentDetailsWidth;
      const ratio = this.currentMainWidth / mainDetailsTotal;
      newMainWidth = availableWidth * ratio;
      newDetailsWidth = availableWidth - newMainWidth;
    } else {
      newMainWidth = availableWidth;
      newDetailsWidth = 0;
    }

    this.currentTreeWidth = newTreeWidth;
    this.currentMainWidth = newMainWidth;
    this.currentDetailsWidth = newDetailsWidth;
    this.updateFlexValues();
    this.startX = event.clientX;
  }

  private handleDetailsResize(event: MouseEvent): void {
    const container = this.adminContainer.nativeElement;
    const containerWidth = container.getBoundingClientRect().width;
    const deltaX = event.clientX - this.startX;
    const percentChange = (deltaX / containerWidth) * 100;

    let newDetailsWidth = this.currentDetailsWidth - percentChange; // Reverse for right-side panel
    newDetailsWidth = Math.max(this.MIN_DETAILS_WIDTH, Math.min(this.MAX_DETAILS_WIDTH, newDetailsWidth));

    const availableWidth = 100 - this.currentTreeWidth - newDetailsWidth;
    const newMainWidth = Math.max(this.MIN_MAIN_WIDTH, availableWidth);

    this.currentMainWidth = newMainWidth;
    this.currentDetailsWidth = newDetailsWidth;
    this.updateFlexValues();
    this.startX = event.clientX;
  }

  private onMouseUp(): void {
    if (this.isTreeResizing || this.isDetailsResizing) {
      this.isTreeResizing = false;
      this.isDetailsResizing = false;
      this.saveLayout();
    }
  }

  private updateFlexValues(): void {
    this.treeAreaFlex = `0 0 ${this.currentTreeWidth}%`;
    this.mainAreaFlex = `0 0 ${this.currentMainWidth}%`;
    this.detailsAreaFlex = `0 0 ${this.currentDetailsWidth}%`;
  }

  // Reset panel sizes
  resetPanelSizes(): void {
    console.log('Resetting admin panel sizes to defaults...');
    this.currentTreeWidth = this.DEFAULT_TREE_WIDTH;
    this.currentMainWidth = this.showDetails ? this.DEFAULT_MAIN_WIDTH : this.DEFAULT_MAIN_NO_DETAILS;
    this.currentDetailsWidth = this.showDetails ? this.DEFAULT_DETAILS_WIDTH : 0;
    this.updateFlexValues();
    this.saveLayout();
    console.log('Admin panel sizes reset to defaults');
  }

  // Tree event handlers
  onNodeSelected(node: AdminTreeNode): void {
    this.selectedNode = node;
    console.log('Node selected:', node);

    // Auto-hide details panel for Image Processing Tool (full width needed)
    if (this.isImageProcessingTool()) {
      if (this.showDetails) {
        this.showDetails = false;
        this.currentMainWidth = this.DEFAULT_MAIN_NO_DETAILS;
        this.currentDetailsWidth = 0;
        this.updateFlexValues();
        this.saveLayout();
      }
    }

    // Auto-load bucket folders for Python Scripts
    if (node.parent_id === 'python-scripts' || node.node_id === 'python-scripts') {
      this.loadBucketFolders();
    }
  }

  /**
   * Check if the current selected node is the Image Processing Tool
   */
  isImageProcessingTool(): boolean {
    return this.selectedNode?.node_id === 'image-processing-tool' || 
           this.selectedNode?.node_id === 'load-image-processing-tool';
  }

  onEditModeToggled(editMode: boolean): void {
    this.showEditMode = editMode;
    console.log('Edit mode toggled:', editMode);
  }

  onNodeAdded(event: {parent: AdminTreeNode, newNode: AdminTreeNode}): void {
    console.log('Node added:', event);
    this.showSnackbar(`Added new node: ${event.newNode.name}`);
  }

  onNodeEdited(node: AdminTreeNode): void {
    console.log('Node edited:', node);
    this.showSnackbar(`Node edited: ${node.name}`);
  }

  onNodeDeleted(node: AdminTreeNode): void {
    console.log('Node deleted:', node);
    this.showSnackbar(`Node deleted: ${node.name}`);
  }

  onTreeRefreshed(): void {
    console.log('Tree refreshed');
    this.showSnackbar('Tree refreshed successfully');
  }

  onTreeCollapseStateChanged(isCollapsed: boolean): void {
    this.isTreeCollapsed = isCollapsed;
    console.log('Tree collapse state changed:', isCollapsed);
    
    // Adjust layout when tree collapse state changes
    this.adjustLayoutForTreeCollapse();
  }

  // Layout adjustment when tree collapses/expands
  private adjustLayoutForTreeCollapse(): void {
    if (this.isTreeCollapsed) {
      // When tree is collapsed, set it to fixed 60px width
      this.treeAreaFlex = '0 0 60px';
      
      // Calculate remaining space percentage for main and details
      // Assume container is roughly 100% minus the 60px for tree (approximately 5% for a 1200px container)
      const remainingSpace = 95; // Approximate percentage after 60px tree
      
      if (this.showDetails) {
        // Split remaining space between main and details based on their default ratio
        const defaultMainDetailsTotal = this.DEFAULT_MAIN_WIDTH + this.DEFAULT_DETAILS_WIDTH;
        const mainRatio = this.DEFAULT_MAIN_WIDTH / defaultMainDetailsTotal;
        const detailsRatio = this.DEFAULT_DETAILS_WIDTH / defaultMainDetailsTotal;
        
        this.mainAreaFlex = `0 0 ${remainingSpace * mainRatio}%`;
        this.detailsAreaFlex = `0 0 ${remainingSpace * detailsRatio}%`;
      } else {
        // If no details, main area takes all remaining space
        this.mainAreaFlex = `0 0 ${remainingSpace}%`;
        this.detailsAreaFlex = '0 0 0%';
      }
    } else {
      // When tree is expanded, restore normal flex-based layout
      this.updateFlexValues();
    }
    
    // Force change detection to update UI
    this.cdr.detectChanges();
    console.log('Layout adjusted for tree collapse state:', {
      isCollapsed: this.isTreeCollapsed,
      treeAreaFlex: this.treeAreaFlex,
      mainAreaFlex: this.mainAreaFlex,
      detailsAreaFlex: this.detailsAreaFlex
    });
  }

  // Main area event handlers
  onFolderSelected(folder: string): void {
    this.selectedFolder = folder;
    console.log('Folder selected:', folder);

    // Auto-load existing processed files for the selected folder
    this.loadExistingProcessedFiles(folder);
  }

  onFolderProcessed(folder: string): void {
    console.log('Processing folder:', folder);
    this.processSelectedFolder();
  }

  onFileSelected(file: ProcessedFile): void {
    this.selectedFile = file;
    console.log('File selected:', file);

    // Auto-show details panel when file is selected
    if (!this.showDetails) {
      this.toggleDetailsPanel();
    }
  }

  onShowDetailsToggled(showDetails: boolean): void {
    this.showDetails = showDetails;
    this.adjustLayoutForDetails();
  }

  onBucketsRefreshed(): void {
    this.loadBucketFolders();
  }

  // Details panel event handlers
  onDetailsClose(): void {
    this.toggleDetailsPanel();
  }

  onShowMainsChange(showMains: boolean): void {
    // Handle show mains change if needed
    console.log('Show mains changed:', showMains);
  }

  onViewModeChange(viewMode: ViewMode): void {
    this.viewMode = viewMode;
    console.log('View mode changed:', viewMode);
  }

  onFileNavigated(direction: 'previous' | 'next'): void {
    console.log('📍 AdminComponent: onFileNavigated received direction:', direction);
    console.log('📍 AdminComponent: selectedNode?.node_id:', this.selectedNode?.node_id);
    console.log('📍 AdminComponent: adminMainArea exists:', !!this.adminMainArea);
    
    // Check if we're in bucket file preview mode (GCP Upload/Modify)
    if (this.selectedNode?.node_id === 'modify/Upload' && this.adminMainArea) {
      console.log('📍 AdminComponent: In GCP mode, calling navigateBucketFile');
      const newFile = this.adminMainArea.navigateBucketFile(direction, this.selectedFile);
      if (newFile) {
        this.selectedFile = newFile;
        console.log('📍 AdminComponent: Updated selectedFile to:', newFile.page_id);
      } else {
        console.log('📍 AdminComponent: navigateBucketFile returned null');
      }
      return;
    }
    
    // Default behavior for processed files
    const currentIndex = this.processedFiles.findIndex(f => f === this.selectedFile);
    if (currentIndex === -1) return;

    let newIndex: number;
    if (direction === 'previous') {
      newIndex = Math.max(0, currentIndex - 1);
    } else {
      newIndex = Math.min(this.processedFiles.length - 1, currentIndex + 1);
    }

    this.selectedFile = this.processedFiles[newIndex];
  }

  // Toggle details panel
  toggleDetailsPanel(): void {
    this.showDetails = !this.showDetails;
    this.adjustLayoutForDetails();
  }

  // Toggle tree panel collapse
  toggleTreeCollapse(): void {
    this.isTreeCollapsed = !this.isTreeCollapsed;
    this.adjustLayoutForTreeCollapse();
  }

  private adjustLayoutForDetails(): void {
    if (this.showDetails) {
      // Give more space to details panel for better image viewing
      this.currentMainWidth = this.DEFAULT_MAIN_WIDTH;
      this.currentDetailsWidth = this.DEFAULT_DETAILS_WIDTH;
      // Tree panel keeps its current width
    } else {
      this.currentMainWidth = this.DEFAULT_MAIN_NO_DETAILS;
      this.currentDetailsWidth = 0;
    }
    
    // If tree is collapsed, use the collapse layout instead
    if (this.isTreeCollapsed) {
      this.adjustLayoutForTreeCollapse();
    } else {
      this.updateFlexValues();
    }
    
    this.saveLayout();
  }

  // Bucket operations (existing functionality)
  loadBucketFolders(): void {
    this.isLoading = true;
    this.bucketService.getFolders(this.bucketName).subscribe({
      next: (response) => {
        this.bucketFolders = response.folders;
        this.isLoading = false;
        console.log('Loaded bucket folders:', response.folders);
      },
      error: (error) => {
        console.error('Error loading bucket folders:', error);
        this.isLoading = false;
        this.showSnackbar('Error loading bucket folders');
      }
    });
  }

  processSelectedFolder(): void {
    if (!this.selectedFolder) return;

    this.isLoading = true;
    this.operationResult = '';

    this.bucketService.processFolders([this.selectedFolder]).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.success && response.processed_files && response.processed_files.length > 0) {
          // Use the bucket service's URL processing instead of our own
          this.processedFiles = response.processed_files;
          this.selectedFile = this.processedFiles[0];
          this.showProcessedFiles = true;
          this.operationResult = `Processing completed for folder: ${this.selectedFolder}\nProcessed ${response.processed_files_count} files successfully.`;
          this.showSnackbar(`Processed ${response.processed_files_count} files successfully`);
        } else {
          this.operationResult = `Processing completed but no files were processed from folder: ${this.selectedFolder}`;
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error processing folder:', error);
        this.operationResult = `Error processing folder: ${this.selectedFolder}\n${error.message || 'Unknown error'}`;
        this.showSnackbar('Error processing folder');
      }
    });
  }

  private loadExistingProcessedFiles(folderName: string): void {
    this.bucketService.getProcessedFiles(folderName).subscribe({
      next: (response) => {
        // Use the bucket service's URL processing instead of our own
        this.processedFiles = response.processed_files;
        // Show processed files section if we have files
        this.showProcessedFiles = this.processedFiles.length > 0;
        console.log('Loaded processed files:', this.processedFiles);

        if (this.processedFiles.length > 0) {
          this.showSnackbar(`Found ${this.processedFiles.length} processed files in folder: ${folderName}`);
        }
      },
      error: (error) => {
        console.error('Error loading processed files:', error);
        this.processedFiles = [];
        this.showProcessedFiles = false;
        // Don't show error snackbar for missing processed files - it's normal
        console.log('No processed files found for folder:', folderName);
      }
    });
  }

  private showSnackbar(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top'
    });
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/']);
  }

  @HostListener('window:resize')
  onResize(): void {
    // Handle window resize if needed
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    // Handle global keyboard shortcuts
    if (event.ctrlKey && event.key === 'd') {
      event.preventDefault();
      this.toggleDetailsPanel();
    }
  }
}
